import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Interface cho thông tin điều khoản sử dụng
 */
export interface TermsItem {
  /** ID duy nhất của điều khoản */
  id: number;
  /** Tiêu đề điều khoản */
  title: string;
  /** Nội dung điều khoản */
  content: string;
  /** Phiên bản điều khoản */
  version: string;
  /** Đường dẫn file (nếu có) */
  filePath?: string;
  /** URL truy cập file (nếu có) */
  fileUrl?: string;
  /** Tên file gốc (nếu có) */
  originalFileName?: string;
  /** Trạng thái hoạt động của điều khoản */
  isActive: boolean;
  /** Thời gian xuất bản (tùy chọn) */
  publishedAt?: string;
  /** Thời gian tạo */
  createdAt: string;
  /** Thời gian cập nhật cuối */
  updatedAt: string;
}

/**
 * Interface cho yêu cầu tạo điều khoản mới
 */
export interface CreateTermsRequest {
  /** Tiêu đề điều khoản */
  title: string;
  /** Nội dung điều khoản */
  content?: string;
  /** Phiên bản điều khoản */
  version: string;
  /** Trạng thái hoạt động (tùy chọn, mặc định false) */
  isActive?: boolean;
}

/**
 * Interface cho yêu cầu cập nhật điều khoản
 */
export interface UpdateTermsRequest {
  /** Tiêu đề điều khoản (tùy chọn) */
  title?: string;
  /** Nội dung điều khoản (tùy chọn) */
  content?: string;
  /** Phiên bản điều khoản (tùy chọn) */
  version?: string;
  /** Trạng thái hoạt động (tùy chọn) */
  isActive?: boolean;
}

/**
 * Service xử lý các chức năng quản lý điều khoản sử dụng
 * Bao gồm xem, tạo, cập nhật, xóa điều khoản
 */
export class TermsService {
  /** URL cơ sở của API */
  private static baseUrl = getApiBase();

  /** Lấy base URL (hữu ích để dựng file URL khi backend chưa trả sẵn) */
  static getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Lấy headers xác thực cho các request API
   * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  // ================== PUBLIC TERMS ==================
  
  /**
   * Lấy điều khoản đang hoạt động (public)
   * @returns Promise<TermsItem | null> - Thông tin điều khoản đang hoạt động hoặc null nếu chưa có
   * @throws Error - Khi không thể tải điều khoản
   */
  static async getActiveTerms(): Promise<TermsItem | null> {
    const response = await fetch(`${this.baseUrl}/terms/active`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      // 404 khi chưa có terms active → trả null để UI hiển thị fallback
      if (response.status === 404) return null;
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải điều khoản.';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }

    return response.json();
  }

  // ================== ADMIN TERMS ==================
  
  /**
   * Lấy danh sách tất cả điều khoản (admin)
   * @returns Promise<TermsItem[]> - Danh sách tất cả điều khoản
   * @throws Error - Khi không thể tải danh sách điều khoản
   */
  static async listTerms(): Promise<TermsItem[]> {
    const response = await apiClient.get('/terms');
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách điều khoản';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Tạo điều khoản mới (admin)
   * @param data - Thông tin điều khoản cần tạo
   * @returns Promise<TermsItem> - Thông tin điều khoản vừa tạo
   * @throws Error - Khi tạo điều khoản thất bại
   */
  static async createTerms(data: CreateTermsRequest): Promise<TermsItem> {
    const response = await apiClient.post('/terms', data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Tạo điều khoản thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Cập nhật thông tin điều khoản (admin)
   * @param id - ID của điều khoản cần cập nhật
   * @param data - Thông tin điều khoản cần cập nhật
   * @returns Promise<TermsItem> - Thông tin điều khoản sau khi cập nhật
   * @throws Error - Khi cập nhật điều khoản thất bại
   */
  static async updateTerms(id: number, data: UpdateTermsRequest): Promise<TermsItem> {
    const response = await apiClient.patch(`/terms/${id}`, data);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật điều khoản thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xóa điều khoản (admin)
   * @param id - ID của điều khoản cần xóa
   * @returns Promise<void>
   * @throws Error - Khi xóa điều khoản thất bại
   */
  static async deleteTerms(id: number): Promise<void> {
    const response = await apiClient.delete(`/terms/${id}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Xóa điều khoản thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  }

  /**
   * Upload tệp đính kèm cho điều khoản (admin)
   * @param id - ID điều khoản
   * @param file - Tệp cần upload
   */
  static async uploadTermsFile(id: number, file: File): Promise<TermsItem> {
    const form = new FormData();
    form.append('file', file);
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await apiClient.request(`/terms/${id}/file`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Upload tệp thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }
}
